/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import * as React from 'react';
import { FlatTableRow, FlatTableRowHeader } from 'carbon-react/lib/components/flat-table';

import { DragHandle, useTreeRow } from '@futrli/ui-components';
import { VisuallyHidden } from '@futrli/ui-components/components/visually-hidden';

import { AccountRow } from './account-row';
import { ActiveMode, COADisplayAccount } from '../../types';
import { DragHandlers } from '../../hooks/useCategoryDragDrop';
import { GroupRowActions } from './group-row-actions';

interface Props {
  row: COADisplayAccount;
  isBalanceSheet: boolean;
  accountNameMap?: Record<string, string>;
  isNested?: boolean;
  activeMode?: ActiveMode;
  categoryName?: string;
  drag: DragHandlers;
  index: number;
  flatIndexMap: Record<string, number>;
  isUpdatingStructure: boolean;
}

interface SubRowsProps {
  children: COADisplayAccount[];
  parentDepth: number;
  isBalanceSheet: boolean;
  accountNameMap?: Record<string, string>;
  activeMode?: ActiveMode;
  categoryName?: string;
  drag: DragHandlers;
  flatIndexMap: Record<string, number>;
  isUpdatingStructure: boolean;
}

const renderSubRows = ({
  children,
  parentDepth,
  isBalanceSheet,
  accountNameMap,
  activeMode,
  categoryName,
  drag,
  flatIndexMap,
  isUpdatingStructure
}: SubRowsProps): React.ReactNode[] =>
  children.map(child => {
    // Calculate child depth based on parent
    const childWithDepth = { ...child, depth: parentDepth + 1 };

    const exactGlobalIndex = flatIndexMap[child.id];

    if (child.type === 'group') {
      return (
        <GroupRow
          key={child.id}
          row={childWithDepth}
          isBalanceSheet={isBalanceSheet}
          accountNameMap={accountNameMap}
          activeMode={activeMode}
          categoryName={categoryName}
          drag={drag}
          index={exactGlobalIndex}
          isNested
          flatIndexMap={flatIndexMap}
          isUpdatingStructure={isUpdatingStructure}
        />
      );
    }
    return (
      <AccountRow
        key={child.id}
        row={childWithDepth}
        isBalanceSheet={isBalanceSheet}
        accountNameMap={accountNameMap}
        isNested
        activeMode={activeMode}
        categoryName={categoryName}
        drag={drag}
        index={exactGlobalIndex}
        isUpdatingStructure={isUpdatingStructure}
      />
    );
  });

export const GroupRow = ({
  row,
  isBalanceSheet,
  accountNameMap,
  isNested,
  index,
  categoryName,
  drag,
  activeMode,
  flatIndexMap,
  isUpdatingStructure
}: Props) => {
  const isReorderingMode = !!(activeMode === ActiveMode.REORDERING && drag && categoryName);

  const { rowRef, indentPx } = useTreeRow({
    depth: row.depth || 0,
    drag,
    index,
    isReordering: isReorderingMode,
    type: row.type
  });

  const subRows =
    row.children && row.children.length > 0
      ? renderSubRows({
          children: row.children,
          parentDepth: row.depth || 0,
          isBalanceSheet,
          accountNameMap,
          activeMode,
          categoryName,
          drag,
          flatIndexMap,
          isUpdatingStructure
        })
      : undefined;

  return (
    <FlatTableRow
      expandable={!isReorderingMode}
      expanded
      subRows={subRows}
      bgColor={isNested ? '#F4F5F6' : undefined}
      ref={rowRef}
      key={row.id}
      id={`${row.type}-${index}-${categoryName}`}
      data-testid={`${row.type}-${index}-${categoryName}`}
      data-element={isUpdatingStructure ? 'updating-coa-row' : undefined}
    >
      {isReorderingMode ? (
        <FlatTableRowHeader pl={`${indentPx}px`}>
          <DragHandle
            className="row-drag-handle"
            drag={drag}
            index={index}
            rowRef={rowRef}
            disabled={isUpdatingStructure}
          />

          <span className="row-text-content" style={{ fontWeight: 500 }}>
            {accountNameMap?.[row.id] || row.name || ''}
          </span>
        </FlatTableRowHeader>
      ) : (
        <FlatTableRowHeader pl={`${indentPx}px`}>
          <span style={{ fontWeight: 500 }}>{accountNameMap?.[row.id] || row.name}</span>
        </FlatTableRowHeader>
      )}
      <FlatTableRowHeader>
        <VisuallyHidden>{accountNameMap?.[row.id] || row.name}</VisuallyHidden>
      </FlatTableRowHeader>
      <FlatTableRowHeader>
        <VisuallyHidden>{accountNameMap?.[row.id] || row.name}</VisuallyHidden>
      </FlatTableRowHeader>
      <FlatTableRowHeader pt="4px" pb="4px" width={132} px="8px">
        <GroupRowActions coaRow={row} drag={drag} index={index} isUpdatingStructure={isUpdatingStructure} />
      </FlatTableRowHeader>
    </FlatTableRow>
  );
};
